/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { parse, stringify } from 'zipson';
import { Names } from './storeName';

// 主流程状态管理
export const useStore = defineStore(Names.STATUS, {
  state: () => ({
    isOutlineGenerating: false, // 大纲是否正在生成
    outlineController: null, // 中断大纲生成
    outlineId: '', // 当前页面大纲id
    suitId: '', // 当前页面模板id
    selectedSuitId: '', // 选中模版id
    presentationId: '', // 当前已生成的ppt id
    currentStep: 'input', // 步骤条状态
    topicInfo: { // 主题生成
      topic: '', // 主题文本
      pageSize: '', // 篇幅设置
      audience: '', // 受众设置
      scene: '', // 场景设置
      verbosity: '', // 单页字数设置
      language: '', // 语言设置
    },
    remainInfo: { // 剩余次数
      createSlideRemain: 0, // ppt
      createOutlineRemain: 0, // 生成大纲
      exportPPTRemain: 0, // 导出
      recreateOutlineRemain: 0, // 换大纲
    },
    isPPTGenerating: false, // PPT 是否正在生成
    isPPTFinish: false, // PPT 是否生成完成
    textInfo: {
      title: '', // 文本生成大纲标题
      context: '', // 文本内容
      pageSize: '', // 篇幅设置
      audience: '', // 受众设置
      scene: '', // 场景设置
      verbosity: '', // 单页字数设置
      language: '', // 语言设置
    },
    documentInfo: {
      documentId: '', // 上传的文档的id
      documentUrl: '', // 上传文档的预览地址
      type: '', // 上传文档的类型
      name: '', // 文件名称
      size: '', // 文件大小
      thumbnail: '', // 预览图
    },
    promptOptions: {
      pageSize: '', // 篇幅设置
      audience: '', // 受众设置
      scene: '', // 场景设置
      verbosity: '', // 单页字数设置
      language: '', // 语言设置
    },
    createType: '', // 生成方式
    canRefresh: false, // 「更新PPT」按钮是否可点击
    showSave: true, // 为true时展示「保存到云」按钮，为false时展示「我的文档」按钮
    isSingleTemplate: false, // 是否「单页美化」
    outlinePortStatus: false, // 大纲接口是否返回状态，false表示正在请求接口，true表示接口开始返回数据
    isPPTGenerationLoading: true, // 是否首次PPT生成过场动画
  }),
  persist: {
    key: 'ai-ppt-store',
    storage: sessionStorage,
    paths: [
      'outlineId',
      'suitId',
      'topicInfo.topic',
      'topicInfo.pageSize',
      'topicInfo.audience',
      'topicInfo.scene',
      'topicInfo.verbosity',
      'topicInfo.language',
      'isPPTFinish',
      'currentStep',
      'textInfo.title',
      'textInfo.context',
      'textInfo.pageSize',
      'textInfo.audience',
      'textInfo.scene',
      'textInfo.verbosity',
      'textInfo.language',
      'remainInfo.createSlideRemain',
      'remainInfo.createOutlineRemain',
      'remainInfo.exportPPTRemain',
      'remainInfo.recreateOutlineRemain',
      'presentationId',
      'canRefresh',
      'createType',
      'showSave',
      'isSingleTemplate',
      'isPPTGenerateLoading',
      'documentInfo.documentId',
      'documentInfo.documentUrl',
      'documentInfo.type',
      'documentInfo.name',
      'documentInfo.size',
      'documentInfo.thumbnail',
    ],
    serializer: {
      deserialize: parse,
      serialize: stringify,
    },
  },

  getters: {
    getOutlineStatus: (state) => state.isOutlineGenerating,
    getCurrentStep: (state) => state.currentStep,
    getTopic: (state) => state.topicInfo.topic,
    getPageSize: (state) => state.topicInfo.pageSize,
    getAudience: (state) => state.topicInfo.audience,
    getScene: (state) => state.topicInfo.scene,
    getVerbosity: (state) => state.topicInfo.verbosity,
    getLanguage: (state) => state.topicInfo.language,
    getOutlineController: (state) => state.outlineController,
    getOutlineId: (state) => state.outlineId,
    getRemain: (state) => state.remainInfo,
    getPPTGenerationStatus: (state) => state.isPPTGenerating,
    getPPTData: (state) => state.PPTData,
    getPPTFinishStatus: (state) => state.isPPTFinish,
    getTextInfo: (type, state) => state[type],
    getSuitId: (state) => state.suitId,
    getSelectedSuitId: (state) => state.selectedSuitId,
    getPresentationId: (state) => state.presentationId,
    getDocumentId: (state) => state.documentInfo.documentId,
    getCanRefresh: (state) => state.canRefresh,
    getCreateType: (state) => state.createType,
    getShowSave: (state) => state.showSave,
    getSingleTemplate: (state) => state.isSingleTemplate,
    getOutlinePortStatus: (state) => state.outlinePortStatus,
    getPPTGenerationLoading: (state) => state.isPPTGenerationLoading,
  },

  actions: {
    changeOutlineStatus(status) {
      this.isOutlineGenerating = status;
    },
    updateStep(status) {
      this.currentStep = status;
    },
    changeTopic(value) {
      this.topicInfo.topic = value;
    },
    changePageSize(value) {
      this.topicInfo.pageSize = value;
    },
    changeAudience(value) {
      this.topicInfo.audience = value;
    },
    changeScene(value) {
      this.topicInfo.scene = value;
    },
    changeVerbosity(value) {
      this.topicInfo.verbosity = value;
    },
    changeLanguage(value) {
      this.topicInfo.language = value;
    },
    changeOutlineController(value) {
      this.outlineController = value;
    },
    saveOutlineId(id) {
      this.outlineId = id;
    },
    changeRemain(type, value) {
      this.remainInfo[type] = value;
    },
    changePPTGenerationStatus(status) {
      this.isPPTGenerating = status;
    },
    changePPTFinishStatus(status) {
      this.isPPTFinish = status;
    },
    changeTextInfo(type, value) {
      this.textInfo[type] = value;
    },
    saveSuitId(id) {
      this.suitId = id;
    },
    changeSelectedSuitId(id) {
      this.selectedSuitId = id;
    },
    savePresentationId(id) {
      this.presentationId = id;
    },
    changeDocumentId(id) {
      this.documentInfo.documentId = id;
    },
    changeDocumentPreview(url) {
      this.documentInfo.documentUrl = url;
    },
    changeDocumentType(type) {
      this.documentInfo.type = type;
    },
    changeDocumentName(name) {
      this.documentInfo.name = name;
    },
    changeDocumentSize(size) {
      this.documentInfo.size = size;
    },
    changeDocumentThumbnail(thumbnail) {
      this.documentInfo.thumbnail = thumbnail;
    },
    changePromptOptions(type, value) {
      this.promptOptions[type] = value;
    },
    changeCanRefresh(value) {
      this.canRefresh = value;
    },
    saveCreateType(type) {
      this.createType = type;
    },
    changeShowSave(value) {
      this.showSave = value;
    },
    changeSingleTemplate(value) {
      this.isSingleTemplate = value;
    },
    changeOutlinePortStatus(status) {
      this.outlinePortStatus = status;
    },
    changePPTGenerationLoading(status) {
      this.isPPTGenerationLoading = status;
    },
  },
});
